import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NAV } from "@/constants/testIds";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const LINKS = [
    { to: "/", label: t("nav.home"), testid: NAV.linkHome },
    { to: "/services", label: t("nav.services"), testid: NAV.linkServices },
    { to: "/case-studies", label: t("nav.caseStudies"), testid: NAV.linkCaseStudies },
    { to: "/about", label: t("nav.about"), testid: NAV.linkAbout },
    { to: "/blog", label: t("nav.blog"), testid: NAV.linkBlog },
    { to: "/contact", label: t("nav.contact"), testid: NAV.linkContact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ink-950/80 backdrop-blur-xl border-b border-white/5"
          : "bg-ink-950/40 backdrop-blur-md"
      }`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" className="group flex items-center gap-2" data-testid={NAV.logo}>
          <span className="text-xl md:text-2xl font-display tracking-tight text-bone-100 leading-none">
            Rev<span className="text-clay-400">Grow</span>
          </span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-widest2 text-bone-200/50 mt-1">
            {t("nav.advisory")}
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-widest2 font-semibold transition-colors duration-300 ${
                  isActive ? "text-clay-400" : "text-bone-100/80 hover:text-bone-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          <Link to="/contact" data-testid={NAV.ctaStart} className="btn-primary group">
            {t("nav.cta")}
            <ArrowUpRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>

        <button
          className="lg:hidden text-bone-100 p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid={NAV.mobileToggle}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-ink-950/95 backdrop-blur-xl border-t border-white/5"
          data-testid={NAV.mobileMenu}
        >
          <nav className="container-x py-8 flex flex-col gap-6">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `text-2xl font-display transition-colors ${
                    isActive ? "text-clay-400" : "text-bone-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-2">
              <LanguageSwitcher variant="mobile" />
            </div>
            <Link
              to="/contact"
              className="btn-primary mt-4 w-fit"
              data-testid={`${NAV.ctaStart}-mobile`}
            >
              {t("nav.cta")}
              <ArrowUpRight size={16} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
